import { ReactNode } from 'react';
import { Box, Typography, Button, styled, Stack } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { QUOIDEN_SUPPORT_EMAIL } from '~/shared/constants';
import { EXTERNAL_URLS } from 'common';
import { ProgressBar } from '~/modules/onboarding/components/ProgressBar';
import { QuoidenLogo } from '~/assets/logos';
import { BackArrow } from '~/assets/icons';

interface Props {
  children: ReactNode;
  showProgress?: boolean;
  progressPercentage?: number;
  showBack?: boolean;
  onBack?: () => void;
  showLogOut?: boolean;
  onLogOut?: () => void;
}

export const OnboardingLayout = ({
  children,
  showProgress = false,
  progressPercentage = 0,
  showBack = false,
  onBack,
  showLogOut = false,
  onLogOut,
}: Props) => {
  return (
    <MainContainer>
      <HeaderContainer direction="row">
        <LeftSection direction="row">
          {showBack && (
            <BackButton onClick={onBack} data-testid="back-button">
              <BackArrow /> Back
            </BackButton>
          )}
        </LeftSection>

        {showProgress && (
          <ProgressContainer>
            <ProgressBar percentage={progressPercentage} showLabel={true} />
          </ProgressContainer>
        )}

        <RightSection direction="row">
          {showLogOut && (
            <LogOutButton onClick={onLogOut} data-testid="logout-button">
              Log Out
            </LogOutButton>
          )}
        </RightSection>
      </HeaderContainer>

      <CenteredContent direction="row">
        <ContentWrapper>{children}</ContentWrapper>
      </CenteredContent>

      <FooterContainer>
        <QuoidenLogoContainer>
          <QuoidenLogo width={146} height={38} />
        </QuoidenLogoContainer>
        <FooterText>
          © {new Date().getFullYear()} Quoiden. All rights reserved |{' '}
          <FooterLink
            href={EXTERNAL_URLS.TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </FooterLink>{' '}
          <FooterLink
            href={EXTERNAL_URLS.PRIVACY_STATEMENT}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Statement
          </FooterLink>{' '}
          <FooterLink
            href={EXTERNAL_URLS.COOKIE_POLICY}
            target="_blank"
            rel="noopener noreferrer"
          >
            Cookie Policy
          </FooterLink>{' '}
          <FooterLink href={`mailto:${QUOIDEN_SUPPORT_EMAIL}`}>
            Contact
          </FooterLink>
        </FooterText>
      </FooterContainer>
    </MainContainer>
  );
};

const MainContainer = styled(Stack)({
  minHeight: '100vh',
  backgroundImage: theme.palette.custom.backgroundGradient,
});

const HeaderContainer = styled(Stack)({
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(9),
  paddingBottom: theme.spacing(15.5),
});

const LeftSection = styled(Stack)({
  minWidth: '100px',
  justifyContent: 'flex-start',
});

const RightSection = styled(Stack)({
  minWidth: '100px',
  justifyContent: 'flex-end',
});

const ProgressContainer = styled(Box)({
  flex: 1,
  minWidth: '200px',
  maxWidth: '400px',
});

const BackButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.custom.textMain,
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  padding: 0,
});

const LogOutButton = styled(Button)({
  color: theme.palette.custom.textMain,
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
});

const CenteredContent = styled(Stack)({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'flex-start',
});

const ContentWrapper = styled(Stack)({
  width: '100%',
  maxWidth: '818px',
  minHeight: 'auto',
  backgroundColor: theme.palette.custom.offWhite,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.custom.lightGrayBrown}`,
  padding: theme.spacing(7, 11, 10.5, 11),
});

const FooterContainer = styled(Stack)({
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  paddingTop: theme.spacing(13),
  overflow: 'visible',
  minWidth: 0,
});

const QuoidenLogoContainer = styled(Box)({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'visible',
  width: '146px',
  height: '38px',
  '& svg': {
    width: '146px',
    height: '38px',
    flexShrink: 0,
    display: 'block',
  },
});

const FooterText = styled(Typography)({
  fontSize: '11px',
  color: theme.palette.custom.darkBrown,
  textAlign: 'center',
  lineHeight: 1.6,
});

const FooterLink = styled('a')({
  color: theme.palette.custom.darkBrown,
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  marginRight: '2px',
});
